/* Event detail + dedicated transaction-entry screens. The entry page is
   tuned for high-throughput data entry from a collection drive: type an ITS
   to load the member, see their last 10 transactions in this scheme + their
   running balance, save deposit/withdrawal, repeat. WhatsApp + preview
   icons surface next to each transaction so confirmations can go out
   immediately. */
const { useState: useStateE, useEffect: useEffectE, useRef: useRefE } = React;

// ─────────── Event detail page ───────────
function EventDetailPage({ eventId, go }) {
  const [event, setEvent] = useStateE(null);
  const [txs, setTxs] = useStateE([]);
  const [busy, setBusy] = useStateE(true);
  const [err, setErr] = useStateE('');
  const [tick, setTick] = useStateE(0);
  const [showEdit, setShowEdit] = useStateE(false);
  const isAdmin = window.QH_DATA?.activeRole?.id === 'admin';

  useEffectE(() => {
    let cancel = false;
    setBusy(true); setErr('');
    Promise.all([
      window.QH_API.get(`/events/${eventId}`),
      window.QH_API.get(`/transactions?eventId=${encodeURIComponent(eventId)}&pageSize=500`),
    ]).then(([ev, txResp]) => {
      if (cancel) return;
      setEvent(ev);
      setTxs(txResp.items || []);
    }).catch(e => { if (!cancel) setErr(e.message || 'Failed to load event.'); })
      .finally(() => { if (!cancel) setBusy(false); });
    return () => { cancel = true; };
  }, [eventId, tick]);

  // The (member, scheme) → current-balance lookup that powers the
  // per-row WhatsApp / preview helpers lives on the store now (see
  // QH_STORE.loadMemberBalances). It's already populated by loadAll()
  // on bootstrap; we just trigger a refresh here so re-entering the
  // page after recording transactions elsewhere shows accurate figures.
  useEffectE(() => {
    window.QH_STORE.loadMemberBalances?.();
  }, [eventId, tick]);

  const reload = () => setTick(t => t + 1);

  if (busy && !event) return <div className="page-body"><div style={{padding:24,color:'var(--ink-3)'}}>Loading…</div></div>;
  if (err) return <div className="page-body"><div style={{padding:14,color:'var(--neg)'}}>{err}</div></div>;
  if (!event) return null;

  const totalIn = txs.filter(t => t.type === 'deposit').reduce((s, t) => s + Number(t.amount), 0);
  const totalOut = txs.filter(t => t.type === 'withdrawal').reduce((s, t) => s + Number(t.amount), 0);
  const closed = event.status === 'closed';

  const closeEvent = async () => {
    if (!window.confirm('Close this event? No further transactions can be added or edited.')) return;
    try {
      await window.QH_API.post(`/events/${eventId}/close`);
      reload();
    } catch (e) { alert(e.message || 'Failed to close event.'); }
  };

  const reopenEvent = async () => {
    if (!window.confirm('Re-open this event? Transactions will become editable again.')) return;
    try {
      await window.QH_API.post(`/events/${eventId}/reopen`);
      reload();
    } catch (e) { alert(e.message || 'Failed to re-open event.'); }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <span className="clickable" onClick={() => go('events')} style={{cursor:'pointer'}}>Events</span>
            <I.ChevronRight s={10}/><span>{event.name}</span>
          </div>
          <h1>{event.name} {closed && <span className="tag" style={{marginLeft:8,verticalAlign:'middle',fontSize:11}}>CLOSED</span>}</h1>
          <div className="subtitle">
            <span className="font-mono">{event.date}</span>
            {event.schemeName && <> · {event.schemeName} <span className="font-mono muted">{event.schemeCode}</span></>}
            {event.defaultBankName && <> · default bank {event.defaultBankName}</>}
            {event.createdByName && <> · by {event.createdByName}</>}
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => go('events')}><I.ArrowRight s={11} style={{transform:'rotate(180deg)'}}/> Back to events</button>
          <button className="btn" onClick={reload} title="Reload data"><I.Refresh/></button>
          {isAdmin && (
            <button className="btn" onClick={() => setShowEdit(true)} title="Edit event metadata (admin only)"><I.Edit s={12}/> Edit event</button>
          )}
          {!closed && window.can('events','edit') && (
            <button className="btn" onClick={closeEvent} title="Seal this event — no further transactions"><I.Check s={12}/> Close event</button>
          )}
          {closed && isAdmin && (
            <button className="btn" onClick={reopenEvent} title="Re-open this event (admin only)"><I.ArrowRight s={11} style={{transform:'rotate(180deg)'}}/> Re-open event</button>
          )}
          {!closed && window.can('transactions','create') && (
            <button className="btn primary" onClick={() => go('eventTx:' + eventId)}><I.Plus/> New transaction</button>
          )}
        </div>
      </div>

      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        {closed && (
          <div style={{padding:'10px 14px',background:'var(--bg-sunken)',borderRadius:6,fontSize:13,display:'flex',alignItems:'center',gap:8}}>
            <I.AlertCircle s={13}/> This event is closed. Transactions can be viewed but not added or modified.
          </div>
        )}
        <KPIStrip items={[
          { label: 'Transactions', value: String(txs.length) },
          { label: 'Deposits', value: '+' + fmtBHD(totalIn) + ' BHD', sub: txs.filter(t => t.type === 'deposit').length + ' entries' },
          { label: 'Withdrawals', value: '−' + fmtBHD(totalOut) + ' BHD', sub: txs.filter(t => t.type === 'withdrawal').length + ' entries' },
          { label: 'Net', value: fmtBHD(totalIn - totalOut) + ' BHD' },
        ]}/>

        <div className="table-wrap" style={{maxHeight:540,overflow:'auto'}}>
          <table className="data">
            <thead style={{position:'sticky',top:0,background:'var(--bg-elev)',zIndex:1}}>
              <tr>
                <th>Date</th><th>In/Out</th><th>ITS</th><th>Member</th>
                <th className="right">Amount · BHD</th><th>Bank</th><th>Maturity</th><th>Comment</th><th>By</th>
                <th style={{width:30}} title="Attached documents"><I.Paperclip s={11}/></th><th></th>
              </tr>
            </thead>
            <tbody>
              {txs.map(t => (
                <EventTxRow key={t.id} tx={t} event={event} closed={closed} onEdited={reload}/>
              ))}
              {txs.length === 0 && (
                <tr><td colSpan={11} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No transactions yet{closed ? '.' : ' — click "New transaction" to record one.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showEdit && (
        <EventEditModal event={event} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); reload(); }}/>
      )}
    </>
  );
}

// ─────────── Edit event metadata (admin only) ───────────
function EventEditModal({ event, onClose, onSaved }) {
  const [name, setName] = useStateE(event.name || '');
  const [date, setDate] = useStateE((event.date || '').slice(0, 10));
  const [schemeId, setSchemeId] = useStateE(event.schemeId || '');
  const [defaultBankId, setDefaultBankId] = useStateE(event.defaultBankId || '');
  const [busy, setBusy] = useStateE(false);
  const [err, setErr] = useStateE('');
  const schemes = window.QH_DATA?.schemes || [];
  const banks = (window.QH_DATA?.banks || []).filter(b => b.active || b.id === event.defaultBankId);

  const submit = async () => {
    if (!name.trim()) { setErr('Event name is required.'); return; }
    if (!date) { setErr('Event date is required.'); return; }
    if (!schemeId) { setErr('Scheme is required.'); return; }
    setBusy(true); setErr('');
    try {
      const body = { name: name.trim(), date };
      // Only send schemeId / defaultBankId when they actually change so
      // the cascade-update on transactions doesn't fire on a no-op edit.
      if (schemeId !== event.schemeId) body.schemeId = schemeId;
      if ((defaultBankId || '') !== (event.defaultBankId || '')) body.defaultBankId = defaultBankId || '';
      await window.QH_API.put(`/events/${event.id}`, body);
      onSaved?.();
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const schemeChanged = schemeId !== event.schemeId;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit event <span className="font-mono muted text-xs" style={{marginLeft: 8}}>{event.id}</span></h3>
          <button className="icon-btn close" onClick={onClose}><I.X/></button>
        </div>
        <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12}}>
          <div className="field"><label>Event name</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} autoFocus/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="field"><label>Date</label>
              <input className="input font-mono" type="date" value={date} onChange={e => setDate(e.target.value)}/>
            </div>
            <div className="field"><label>Default bank</label>
              <select className="select" value={defaultBankId} onChange={e => setDefaultBankId(e.target.value)}>
                <option value="">— None —</option>
                {banks.map(b => <option key={b.id} value={b.id}>{b.name}{b.code ? ` · ${b.code}` : ''}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label>Scheme</label>
            <select className="select" value={schemeId} onChange={e => setSchemeId(e.target.value)}>
              {schemes.map(s => <option key={s.id} value={s.id}>{s.name} · {s.code}</option>)}
            </select>
            {schemeChanged && (
              <div className="text-xs" style={{marginTop:6,color:'var(--warn)',lineHeight:1.5}}>
                <I.AlertCircle s={11}/> Moving the event to a different scheme also re-tags every transaction in this event with the new scheme. Make sure that's intended.
              </div>
            )}
          </div>
          {err && <div className="text-xs" style={{color:'var(--neg)'}}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Single transaction row with WhatsApp + preview + edit ───────────
function EventTxRow({ tx, event, closed, onEdited }) {
  const [showPreview, setShowPreview] = useStateE(false);
  const [showEdit, setShowEdit] = useStateE(false);
  const canEdit = !closed && window.can('transactions', 'edit');
  return (
    <>
      <tr>
        <td className="font-mono text-xs">{tx.date?.slice(0,16) || ''}</td>
        <td>
          {tx.type === 'deposit'
            ? <span className="tag pos" style={{textTransform:'none'}}><I.ArrowDown s={10}/> in</span>
            : <span className="tag neg" style={{textTransform:'none'}}><I.ArrowUp s={10}/> out</span>}
        </td>
        <td className="font-mono text-xs">{tx.memberItsNo || '—'}</td>
        <td>{tx.member}</td>
        <td className={`num right ${tx.type === 'deposit' ? 'pos' : 'neg'}`} style={{fontWeight:600}}>
          {tx.type === 'deposit' ? '+' : '−'}{fmtBHD(tx.amount)}
        </td>
        <td className="text-xs">{tx.bank || '—'}</td>
        <td className="font-mono text-xs muted">
          {tx.maturity
            ? <span title={tx.commitmentMonths ? `${tx.commitmentMonths}m commitment` : ''}>{tx.maturity}</span>
            : '—'}
        </td>
        <td className="text-xs muted">{tx.note || '—'}</td>
        <td className="text-xs muted">{tx.operatorName || tx.operator || '—'}</td>
        <td style={{textAlign:'center'}}>
          {tx.documentCount > 0 ? (
            <button className="icon-btn" title={`${tx.documentCount} document(s) attached — open transaction to view`} onClick={() => setShowEdit(true)}>
              <I.Paperclip s={11}/>
            </button>
          ) : <span className="muted text-xs">—</span>}
        </td>
        <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
          {canEdit && (
            <button className="icon-btn" title="Edit transaction" onClick={() => setShowEdit(true)}><I.Edit s={12}/></button>
          )}
          <button className="icon-btn" title="Preview WhatsApp message" onClick={() => setShowPreview(true)}><I.MessageSquare s={12}/></button>
          <a className="icon-btn" title="Send via WhatsApp" target="_blank" rel="noreferrer"
             style={{color:'#25D366',textDecoration:'none'}}
             href={buildWhatsAppLink(tx, event)}><I.WhatsApp s={12}/></a>
        </td>
      </tr>
      {showPreview && (
        <MessagePreviewModal tx={tx} event={event} onClose={() => setShowPreview(false)}/>
      )}
      {showEdit && (
        <TransactionEditModal tx={tx} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); onEdited?.(); }}/>
      )}
    </>
  );
}

// ─────────── Edit modal ───────────
function TransactionEditModal({ tx, onClose, onSaved }) {
  const [type, setType] = useStateE(tx.type || 'deposit');
  const [amount, setAmount] = useStateE(String(tx.amount ?? ''));
  const [bank, setBank] = useStateE(tx.bank || '');
  const [note, setNote] = useStateE(tx.note || '');
  const [matKind, setMatKind] = useStateE(tx.commitmentMonths ? 'commitment' : 'normal');
  const [matMonths, setMatMonths] = useStateE(String(tx.commitmentMonths || 24));
  const [busy, setBusy] = useStateE(false);
  const [err, setErr] = useStateE('');
  const banks = (window.QH_DATA?.banks || []).filter(b => b.active || b.name === tx.bank);

  const save = async () => {
    if (!Number(amount) || Number(amount) <= 0) { setErr('Amount must be a positive number.'); return; }
    if (!bank) { setErr('Bank is required.'); return; }
    const body = {
      type, amount: Number(amount), bank,
      note: note || '',
      clearCommitment: false,
      commitmentMonths: null,
    };
    if (type === 'deposit') {
      if (matKind === 'commitment') {
        const m = Number(matMonths);
        if (!Number.isFinite(m) || m < 1) { setErr('Commitment months must be at least 1.'); return; }
        body.commitmentMonths = m;
      } else {
        body.clearCommitment = true;
      }
    }
    setBusy(true); setErr('');
    try {
      await window.QH_API.put(`/transactions/${tx.id}`, body);
      // Edits move totals across schemes + dashboards. Trigger the
      // global store reload so other pages see the new state without
      // needing a manual refresh.
      window.QH_STORE.reload?.().catch(() => {});
      onSaved?.();
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit transaction <span className="font-mono muted text-xs" style={{marginLeft:8}}>{tx.id}</span></h3>
          <button className="icon-btn close" onClick={onClose}><I.X/></button>
        </div>
        <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12}}>
          <div className="text-xs muted">{tx.member}{tx.memberItsNo && <> · ITS <span className="font-mono">{tx.memberItsNo}</span></>} · <span className="font-mono">{(tx.date || '').slice(0,16)}</span></div>
          <div style={{display:'flex',gap:6}}>
            <button onClick={() => setType('deposit')} className={`btn ${type === 'deposit' ? 'accent' : ''}`} style={{flex:1,justifyContent:'center',height:38}}><I.ArrowDown s={12}/> Deposit</button>
            <button onClick={() => setType('withdrawal')} className={`btn ${type === 'withdrawal' ? 'accent' : ''}`} style={{flex:1,justifyContent:'center',height:38}}><I.ArrowUp s={12}/> Withdrawal</button>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="field"><label>Amount (BHD)</label>
              <input className="input font-mono" value={amount} onChange={e => setAmount(e.target.value)} style={{fontSize:16,fontWeight:600}}/>
            </div>
            <div className="field"><label>Bank</label>
              <select className="select" value={bank} onChange={e => setBank(e.target.value)}>
                <option value="">— Select —</option>
                {banks.map(b => <option key={b.id} value={b.name}>{b.name}{b.code ? ` · ${b.code}` : ''}</option>)}
              </select>
            </div>
          </div>
          {type === 'deposit' && (
            <div style={{padding:12,background:'var(--bg-sunken)',borderRadius:4}}>
              <div className="text-xs uppercase muted" style={{marginBottom:8}}>Maturity</div>
              <div style={{display:'flex',gap:14,alignItems:'center',flexWrap:'wrap'}}>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}>
                  <input type="radio" name="editMatKind" checked={matKind === 'normal'} onChange={() => setMatKind('normal')}/>
                  Normal · no maturity
                </label>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}>
                  <input type="radio" name="editMatKind" checked={matKind === 'commitment'} onChange={() => setMatKind('commitment')}/>
                  Commitment
                </label>
                {matKind === 'commitment' && (
                  <div style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
                    <input className="input font-mono" style={{width:70,height:28}} value={matMonths}
                           onChange={e => setMatMonths(e.target.value.replace(/\D+/g,''))}/>
                    <span className="muted">months (≥ 1)</span>
                  </div>
                )}
              </div>
              {tx.maturity && matKind === 'commitment' && (
                <div className="text-xs muted" style={{marginTop:6}}>Current maturity: <span className="font-mono">{tx.maturity}</span> — changing months recomputes from the original transaction date.</div>
              )}
            </div>
          )}
          <div className="field"><label>Comment</label>
            <input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note"/>
          </div>
          <window.DocumentAttachments
            entityType="transaction"
            entityId={tx.id}
            editable={window.can('transactions','edit')}/>
          {err && <div className="text-xs" style={{color:'var(--neg)'}}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={busy}><I.Check/> {busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Render the right template body for a tx + open in WhatsApp ───────────
function pickTemplate(trigger, schemeId) {
  const tpls = window.QH_DATA?.templates || [];
  // Defensive: trim + lowercase the trigger comparison so legacy data
  // with "Deposit", " deposit ", or weird casing still matches.
  const wantTrigger = String(trigger || '').trim().toLowerCase();
  const triggersMatch = (t) => String(t.trigger || '').trim().toLowerCase() === wantTrigger;
  // Prefer a scheme-specific template, fall back to the global one.
  return tpls.find(t => triggersMatch(t) && t.schemeId === schemeId)
      || tpls.find(t => triggersMatch(t) && !t.schemeId)
      || null;
}
function renderTemplate(body, tokens) {
  let s = body || '';
  // Conditional blocks: {{#key}}…{{/key}} keep their inner text only when
  // tokens[key] is non-empty. Lets templates wrap optional sentences (e.g.
  // the maturity reminder) so they disappear cleanly on non-commitment
  // transactions instead of leaving "Maturity: —" hanging at the end.
  // Nested blocks of the same key don't parse — the regex is greedy on the
  // outer pair. Different keys can sit side by side or in sequence.
  s = s.replace(/\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_, key, inner) => {
    const v = tokens[key];
    return (v == null || v === '') ? '' : inner;
  });
  for (const [k, v] of Object.entries(tokens)) {
    s = s.split('{{' + k + '}}').join(v ?? '');
  }
  return s;
}
window.renderTemplate = renderTemplate;
function buildMessage(tx, event) {
  const trigger = tx.type === 'deposit' ? 'deposit' : 'withdrawal';
  const tpl = pickTemplate(trigger, tx.schemeId || event?.schemeId);
  const balance = memberBalanceForMessage(tx);
  const sch = (window.QH_DATA?.schemes || []).find(x => x.id === tx.schemeId);
  const tokens = {
    member: tx.member,
    amount: Number(tx.amount).toFixed(3),
    scheme: event?.schemeName || sch?.name || '',
    date: (tx.date || '').slice(0,10),
    txn_id: tx.id,
    // Empty (not "—") when there's no value so any
    // {{#key}}…{{/key}} block wrapping the variable drops cleanly.
    maturity: tx.maturity || '',
    member_balance: balance != null ? balance.toFixed(3) : '',
  };
  if (tpl) return renderTemplate(tpl.body, tokens);
  // No template matched. Render a sensible deposit/withdrawal-aware
  // fallback that mirrors the canonical seeded body shape so the
  // operator still gets a useful message — and crucially, one that's
  // CORRECT for the transaction type, not a generic "recorded" string.
  // Triggers a template authoring nudge in the preview modal too.
  const verb = trigger === 'deposit' ? 'deposit' : 'withdrawal';
  const direction = trigger === 'deposit' ? 'received' : 'processed';
  const balanceLine = tokens.member_balance ? ` Your new balance is ${tokens.member_balance} BHD.` : '';
  const maturityLine = tokens.maturity ? ` Maturity: ${tokens.maturity}.` : '';
  const schemeBit = tokens.scheme ? ` to ${tokens.scheme}` : '';
  return `Dear ${tokens.member}, your ${verb} of BHD ${tokens.amount}${schemeBit} on ${tokens.date} has been ${direction}.${balanceLine} Reference: ${tokens.txn_id}.${maturityLine}`;
}
// Resolve the member's balance for use in a notification message.
//
// IMPORTANT: this is the member's CURRENT balance in the scheme, not
// "balance as of this transaction". Two reasons:
//   1. The WhatsApp / preview UI is for re-sharing — operators want to
//      tell the member "your new balance is X" right now.
//   2. Computing per-tx running balance would require either fetching
//      the member's full scheme history (heavy on cross-member screens
//      like EventDetail) or a server-side window function.
//
// The Record-transaction screen caches one member's full tx list and
// can compute an exact "as of this tx" balance — we honour that when
// the cache covers the (member, scheme). Otherwise we fall back to the
// store's cross-tab lookup (current balance per (member, scheme)) which
// EventDetailPage primes via QH_STORE.loadMemberBalances().
//
// Returns null when neither source has the pair, in which case the
// caller emits an empty token and any {{#member_balance}}…{{/member_balance}}
// template block drops out cleanly.
function memberBalanceForMessage(tx) {
  if (!tx?.memberItsNo || !tx?.schemeId) return null;
  const txCache = window.__memberTxsCache;
  if (txCache && txCache.itsNo === tx.memberItsNo && txCache.schemeId === tx.schemeId) {
    const sorted = [...txCache.txs].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    let bal = 0;
    for (const t of sorted) {
      bal += t.type === 'deposit' ? Number(t.amount) : -Number(t.amount);
      if (t.id === tx.id) return bal;
    }
  }
  const map = window.QH_DATA?.memberSchemeBalances;
  if (map) {
    const key = `${tx.memberItsNo}_${tx.schemeId}`;
    if (key in map) return map[key];
  }
  return null;
}
function memberMobile(itsNo) {
  const m = (window.QH_DATA?.members || []).find(x => x.itsNo === itsNo);
  return m?.mobile1 || '';
}
function buildWhatsAppLink(tx, event) {
  const phone = String(memberMobile(tx.memberItsNo) || '').replace(/\D+/g, '');
  const text = buildMessage(tx, event);
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
}

// ─────────── Preview modal ───────────
function MessagePreviewModal({ tx, event, onClose }) {
  const text = buildMessage(tx, event);
  const phone = memberMobile(tx.memberItsNo);
  const tpl = pickTemplate(tx.type === 'deposit' ? 'deposit' : 'withdrawal', tx.schemeId || event?.schemeId);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>WhatsApp message preview</h3>
          <button className="icon-btn close" onClick={onClose}><I.X/></button>
        </div>
        <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:10}}>
          <div className="text-xs muted">
            Template: <b>{tpl?.name || '(fallback — no template configured for ' + (tx.type === 'deposit' ? 'deposit' : 'withdrawal') + ')'}</b>
            {phone && <> · To: <span className="font-mono">{phone}</span></>}
          </div>
          <div style={{padding:14,background:'var(--bg-sunken)',borderRadius:6,fontSize:13,lineHeight:1.55,whiteSpace:'pre-wrap'}}>
            {text}
          </div>
          {!tpl && (
            <div className="text-xs" style={{color:'var(--warn)'}}>
              <I.AlertCircle s={11}/> No <b>{tx.type}</b> template found. Check Notifications → Trigger field is set on the template you want to use.
            </div>
          )}
          {!phone && <div className="text-xs" style={{color:'var(--warn)'}}><I.AlertCircle s={11}/> No mobile number on file for this member.</div>}
        </div>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose}>Close</button>
          <div style={{flex:1}}/>
          <a className="btn primary" target="_blank" rel="noreferrer"
             href={buildWhatsAppLink(tx, event)}
             style={{background:'#25D366',borderColor:'#25D366',textDecoration:'none'}}>
            <I.WhatsApp s={13}/> Open WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

// ─────────── Dedicated entry page ───────────
function EventTransactionPage({ eventId, go }) {
  const [event, setEvent] = useStateE(null);
  const [busy, setBusy] = useStateE(false);
  const [loadErr, setLoadErr] = useStateE('');

  // Member picker state (search by ITS or name)
  const [member, setMember] = useStateE(null);

  // Member's transactions in this scheme (for the KPIs + this-event list)
  const [memberTxs, setMemberTxs] = useStateE([]);

  // Form state
  const [type, setType] = useStateE('deposit');
  const [amount, setAmount] = useStateE('');
  const [bank, setBank] = useStateE('');
  const [note, setNote] = useStateE('');
  // Per-transaction maturity. Default Normal; Commitment requires months >= 1.
  const [matKind, setMatKind] = useStateE('normal');
  const [matMonths, setMatMonths] = useStateE('24');
  const [submitting, setSubmitting] = useStateE(false);
  const [submitErr, setSubmitErr] = useStateE('');
  const docsRef = React.useRef(null);

  // Initial load: event
  useEffectE(() => {
    let cancel = false;
    setBusy(true); setLoadErr('');
    window.QH_API.get(`/events/${eventId}`)
      .then(ev => {
        if (cancel) return;
        setEvent(ev);
        setBank(ev.defaultBankName || ((window.QH_DATA?.banks || []).find(b => b.active)?.name) || '');
      })
      .catch(e => { if (!cancel) setLoadErr(e.message || 'Failed to load event.'); })
      .finally(() => { if (!cancel) setBusy(false); });
    return () => { cancel = true; };
  }, [eventId]);

  // When a member is picked, pull their full scheme history so we can split
  // it into "before this event" (opening balance) and "in this event" (B).
  useEffectE(() => {
    let cancel = false;
    setMemberTxs([]);
    if (!member?.itsNo || !event) return;
    window.QH_API.get(`/transactions?schemeId=${event.schemeId}&memberItsNo=${member.itsNo}&pageSize=1000`)
      .then(list => { if (!cancel) setMemberTxs(list.items || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [member?.itsNo, event?.id]);

  // Stash the loaded list at window scope so buildMessage() (called from the
  // per-row WhatsApp button) can resolve {{member_balance}} without prop
  // drilling. Cleared on unmount so other screens don't see stale data.
  useEffectE(() => {
    if (member?.itsNo && event && memberTxs.length > 0) {
      window.__memberTxsCache = { itsNo: member.itsNo, schemeId: event.schemeId, txs: memberTxs };
    } else {
      window.__memberTxsCache = null;
    }
    return () => { window.__memberTxsCache = null; };
  }, [member?.itsNo, event?.id, memberTxs]);

  // Compute the three KPI values:
  //   A = opening balance — member's signed sum across all OTHER tx in this
  //       scheme (i.e. everything not tied to this event). Bare un-evented
  //       tx and tx in past events both contribute. Not filtered by date so
  //       retrospective entries on past events flow through immediately.
  //   B = net for THIS event — signed sum of member tx tied to event.id.
  //   C = closing balance = A + B (member's total balance in the scheme).
  const isThisEventTx = (t) => t.eventId === event?.id;
  const signed = (t) => (t.type === 'deposit' ? +Number(t.amount) : -Number(t.amount));
  const thisEventTxs = memberTxs.filter(isThisEventTx);
  const otherTxs = memberTxs.filter(t => !isThisEventTx(t));
  const opening = otherTxs.reduce((s, t) => s + signed(t), 0);
  const thisEventNet = thisEventTxs.reduce((s, t) => s + signed(t), 0);
  const thisEventDeposits = thisEventTxs.filter(t => t.type === 'deposit').reduce((s, t) => s + Number(t.amount), 0);
  const thisEventWithdrawals = thisEventTxs.filter(t => t.type === 'withdrawal').reduce((s, t) => s + Number(t.amount), 0);
  const closing = opening + thisEventNet;

  const closed = event?.status === 'closed';

  const submit = async () => {
    if (!member?.itsNo) { setSubmitErr('Pick a member first.'); return; }
    if (!Number(amount) || Number(amount) <= 0) { setSubmitErr('Amount must be a positive number.'); return; }
    if (!bank) { setSubmitErr('Pick a bank.'); return; }
    let commitmentMonths = null;
    if (type === 'deposit' && matKind === 'commitment') {
      const m = Number(matMonths);
      if (!Number.isFinite(m) || m < 1) { setSubmitErr('Commitment months must be at least 1.'); return; }
      commitmentMonths = m;
    }
    setSubmitting(true); setSubmitErr('');
    try {
      const created = await window.QH_API.post('/transactions', {
        schemeId: event.schemeId, eventId: event.id,
        memberItsNo: member.itsNo, memberName: member.fullName,
        type, amount: Number(amount), bank, commitmentMonths,
        note: note || null,
        notifyWhatsapp: false, notifyEmail: false, // operator triggers WA from row icon
      });
      // Flush any documents the operator picked before the tx existed.
      if (docsRef.current?.hasStaged() && created?.id) {
        try { await docsRef.current.flushStaged(created.id); }
        catch (e) { setSubmitErr('Transaction saved but document upload failed: ' + (e.message || 'unknown')); }
      }
      // Optimistic refresh: prepend to memberTxs so the KPIs roll forward.
      setMemberTxs(prev => [created, ...prev]);
      // Clear amount/note + reset maturity to default for next entry; keep
      // member loaded so multiple entries for the same person are quick.
      setAmount(''); setNote(''); setMatKind('normal');
      // Cross-page propagation: a new tx changes scheme totals + member
      // balances + dashboard KPIs. Without this, those views stay stale
      // until the operator reloads. Fire-and-forget — the local panel is
      // already up-to-date from the optimistic prepend.
      window.QH_STORE.reload?.().catch(() => {});
      // Refresh event header counters too.
      try {
        const ev = await window.QH_API.get(`/events/${eventId}`);
        setEvent(ev);
      } catch {}
    } catch (e) { setSubmitErr(e.message || 'Save failed.'); }
    finally { setSubmitting(false); }
  };

  const refreshMemberTxs = async () => {
    if (!member?.itsNo || !event) return;
    try {
      const list = await window.QH_API.get(`/transactions?schemeId=${event.schemeId}&memberItsNo=${member.itsNo}&pageSize=1000`);
      setMemberTxs(list.items || []);
    } catch {}
  };

  if (busy && !event) return <div className="page-body"><div style={{padding:24,color:'var(--ink-3)'}}>Loading…</div></div>;
  if (loadErr) return <div className="page-body"><div style={{padding:14,color:'var(--neg)'}}>{loadErr}</div></div>;
  if (!event) return null;

  const banks = (window.QH_DATA?.banks || []).filter(b => b.active);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <span className="clickable" onClick={() => go('events')} style={{cursor:'pointer'}}>Events</span>
            <I.ChevronRight s={10}/>
            <span className="clickable" onClick={() => go('event:' + eventId)} style={{cursor:'pointer'}}>{event.name}</span>
            <I.ChevronRight s={10}/>
            <span>Record transaction</span>
          </div>
          <h1>Record transaction {closed && <span className="tag" style={{marginLeft:8,verticalAlign:'middle',fontSize:11}}>CLOSED</span>}</h1>
          <div className="subtitle" style={{display:'flex',flexWrap:'wrap',gap:'4px 16px',alignItems:'baseline'}}>
            <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>Scheme </span><b>{event.schemeName || '—'}</b>{event.schemeCode && <span className="font-mono muted" style={{marginLeft:4}}>{event.schemeCode}</span>}</span>
            <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>Event </span><b>{event.name}</b></span>
            <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>Date </span><span className="font-mono">{event.date}</span></span>
            {event.defaultBankName && <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>Default bank </span>{event.defaultBankName}</span>}
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => go('event:' + eventId)}><I.ArrowRight s={11} style={{transform:'rotate(180deg)'}}/> Back to event</button>
          <button className="btn" onClick={async () => {
            try {
              const ev = await window.QH_API.get(`/events/${eventId}`);
              setEvent(ev);
              await refreshMemberTxs();
              window.QH_STORE.reload();
            } catch {}
          }} title="Reload data"><I.Refresh/></button>
          <button className="btn ghost" onClick={() => go('events')}>Back to events list</button>
        </div>
      </div>

      {closed && (
        <div style={{margin:'0 14px 12px',padding:'10px 14px',background:'var(--bg-sunken)',borderRadius:6,fontSize:13,display:'flex',alignItems:'center',gap:8}}>
          <I.AlertCircle s={13}/> This event is closed. Transactions can only be viewed.
        </div>
      )}

      <div className="page-body" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,alignItems:'flex-start'}}>
        {/* Left: form */}
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div className="card">
            <div className="card-header">Member</div>
            <div className="card-body" style={{display:'flex',flexDirection:'column',gap:10}}>
              <MemberSearchPicker value={member} onChange={setMember} disabled={closed}/>
              {member && (
                <div style={{padding:10,background:'var(--bg-sunken)',borderRadius:6,fontSize:13}}>
                  <div style={{fontWeight:600,fontSize:15}}>{member.fullName}</div>
                  <div className="text-xs muted" style={{marginTop:2}}>
                    ITS <span className="font-mono">{member.itsNo}</span>
                    {member.cpr && <> · CPR <span className="font-mono">{member.cpr}</span></>}
                    {member.mobile1 && <> · <span className="font-mono">{member.mobile1}</span></>}
                    {member.email1 && <> · {member.email1}</>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {member && !closed && (
            <div className="card">
              <div className="card-header">Entry</div>
              <div className="card-body" style={{display:'flex',flexDirection:'column',gap:10}}>
                <div style={{display:'flex',gap:6}}>
                  <button onClick={() => setType('deposit')}
                          className={`btn ${type === 'deposit' ? 'accent' : ''}`}
                          style={{flex:1,justifyContent:'center',height:38}}>
                    <I.ArrowDown s={12}/> Deposit
                  </button>
                  <button onClick={() => setType('withdrawal')}
                          className={`btn ${type === 'withdrawal' ? 'accent' : ''}`}
                          style={{flex:1,justifyContent:'center',height:38}}>
                    <I.ArrowUp s={12}/> Withdrawal
                  </button>
                </div>
                <div className="field"><label>Amount (BHD)</label>
                  <input className="input font-mono" value={amount} placeholder="0.000"
                         onChange={e => setAmount(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter' && !submitting) submit(); }}
                         style={{fontSize:18,fontWeight:600}}/>
                </div>
                <div className="field"><label>Bank</label>
                  <select className="select" value={bank} onChange={e => setBank(e.target.value)}>
                    <option value="">— Select —</option>
                    {banks.map(b => <option key={b.id} value={b.name}>{b.name}{b.code ? ` · ${b.code}` : ''}</option>)}
                  </select>
                </div>
                {type === 'deposit' && (
                  <div className="field">
                    <label>Maturity</label>
                    <div style={{display:'flex',gap:14,alignItems:'center',flexWrap:'wrap'}}>
                      <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}>
                        <input type="radio" name="evMatKind" checked={matKind === 'normal'} onChange={() => setMatKind('normal')}/>
                        Normal · no maturity
                      </label>
                      <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}>
                        <input type="radio" name="evMatKind" checked={matKind === 'commitment'} onChange={() => setMatKind('commitment')}/>
                        Commitment
                      </label>
                      {matKind === 'commitment' && (
                        <div style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
                          <input className="input font-mono" style={{width:70,height:28}} value={matMonths}
                                 onChange={e => setMatMonths(e.target.value.replace(/\D+/g,''))}/>
                          <span className="muted">months (≥ 1)</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="field"><label>Comment <span className="text-xs muted" style={{textTransform:'none',letterSpacing:0}}>(optional)</span></label>
                  <input className="input" value={note} onChange={e => setNote(e.target.value)}
                         placeholder="e.g. cheque #, voucher ref, special note"/>
                </div>
                <window.DocumentAttachments ref={docsRef}
                  entityType="transaction"
                  entityId={null}
                  editable={window.can('transactions','create')}
                  title="Attach documents (optional)"
                  emptyMsg="Pick one or more files to upload with this transaction."/>
                {submitErr && <div className="text-xs" style={{color:'var(--neg)'}}>{submitErr}</div>}
                <div style={{display:'flex',gap:6}}>
                  <button className="btn ghost" onClick={() => { setMember(null); setMemberTxs([]); }}>Clear member</button>
                  <div style={{flex:1}}/>
                  <button className="btn primary" onClick={submit} disabled={submitting || !amount}>
                    <I.Check/> {submitting ? 'Saving…' : `Save ${type}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: opening balance, this-event movements, closing balance + this-event tx list */}
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {member && (
            <div className="card">
              <div className="card-header">{member.fullName} · in {event.schemeName || event.schemeCode}</div>
              <div className="card-body">
                <KPIStrip items={[
                  { label: 'Opening balance', value: fmtBHD(opening) + ' BHD', sub: 'across other events in this scheme' },
                  { label: 'For this event', value: (thisEventNet >= 0 ? '+' : '−') + fmtBHD(Math.abs(thisEventNet)) + ' BHD',
                    sub: `+${fmtBHD(thisEventDeposits)} / −${fmtBHD(thisEventWithdrawals)}` },
                  { label: 'Closing balance', value: fmtBHD(closing) + ' BHD' },
                ]}/>
              </div>
              <div className="text-xs uppercase muted" style={{padding:'10px 14px 4px'}}>This scheme entries by this member · last 10</div>
              <div className="table-wrap" style={{border:'none',borderRadius:0,maxHeight:320,overflow:'auto'}}>
                <table className="data">
                  <thead><tr><th>Date</th><th>Event</th><th>Type</th><th className="right">BHD</th><th>Bank</th><th>Comment</th><th></th></tr></thead>
                  <tbody>
                    {/* Most recent first across every event in this scheme. */}
                    {/* Edit is gated by the row's own event status — a row */}
                    {/* belonging to another closed event is read-only too. */}
                    {[...memberTxs].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 10).map(t => {
                      const isCurrent = t.eventId === event.id;
                      const rowEv = isCurrent ? null : (window.QH_DATA?.events || []).find(x => x.id === t.eventId);
                      const rowClosed = isCurrent ? closed : rowEv?.status === 'closed';
                      return (
                        <EventTxRowCompact key={t.id} tx={t} event={event}
                          closed={rowClosed}
                          onEdited={refreshMemberTxs}/>
                      );
                    })}
                    {memberTxs.length === 0 && (
                      <tr><td colSpan={7} style={{textAlign:'center',padding:18,color:'var(--ink-3)',fontSize:12}}>No entries for this member in this scheme yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────── Member search/picker — accepts ITS or name ───────────
// Uses the lightweight /members/search typeahead. Debounced 200 ms; ESC closes.
function MemberSearchPicker({ value, onChange, disabled }) {
  const [q, setQ] = useStateE('');
  const [results, setResults] = useStateE([]);
  const [open, setOpen] = useStateE(false);
  const [loading, setLoading] = useStateE(false);
  const [highlight, setHighlight] = useStateE(0);
  const inputRef = useRefE(null);

  useEffectE(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const list = await window.QH_API.get(`/members/search?q=${encodeURIComponent(q.trim())}`);
        if (!cancel) {
          setResults(Array.isArray(list) ? list : []);
          setHighlight(0);
        }
      } catch { if (!cancel) setResults([]); }
      finally { if (!cancel) setLoading(false); }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);

  const pick = (m) => {
    onChange?.(m);
    setQ('');
    setResults([]);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[highlight]) pick(results[highlight]); }
  };

  if (value) {
    return (
      <div className="field">
        <label>Member</label>
        <div style={{display:'flex',gap:6}}>
          <input className="input" value={`${value.fullName} · ITS ${value.itsNo}`} readOnly
                 style={{flex:1,fontWeight:600}}/>
          <button className="btn ghost" disabled={disabled} onClick={() => onChange?.(null)}>Change</button>
        </div>
      </div>
    );
  }

  return (
    <div className="field" style={{position:'relative'}}>
      <label>Search by ITS No. or name</label>
      <input ref={inputRef} className="input" value={q} disabled={disabled}
             placeholder="Type ITS or name (min 2 chars)…"
             onChange={e => { setQ(e.target.value); setOpen(true); }}
             onFocus={() => setOpen(true)}
             onBlur={() => setTimeout(() => setOpen(false), 150)}
             onKeyDown={onKey}
             style={{fontSize:15}}/>
      {open && q.trim().length >= 2 && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:20,background:'var(--bg-elev)',border:'1px solid var(--line)',borderRadius:6,marginTop:4,maxHeight:280,overflow:'auto',boxShadow:'0 6px 20px rgba(0,0,0,0.08)'}}>
          {loading && <div style={{padding:'10px 12px',fontSize:12,color:'var(--ink-3)'}}>Searching…</div>}
          {!loading && results.length === 0 && <div style={{padding:'10px 12px',fontSize:12,color:'var(--ink-3)'}}>No matches.</div>}
          {!loading && results.map((m, i) => (
            <div key={m.itsNo}
                 onMouseDown={(e) => { e.preventDefault(); pick(m); }}
                 onMouseEnter={() => setHighlight(i)}
                 style={{
                   padding:'8px 12px', cursor:'pointer',
                   background: i === highlight ? 'var(--bg-sunken)' : 'transparent',
                   borderBottom: '1px solid var(--line)',
                 }}>
              <div style={{display:'flex',justifyContent:'space-between',gap:10,fontSize:13}}>
                <span style={{fontWeight:500}}>{m.fullName}</span>
                <span className="font-mono muted text-xs">ITS {m.itsNo}</span>
              </div>
              <div className="text-xs muted" style={{marginTop:2}}>
                {[m.cpr && `CPR ${m.cpr}`, m.mobile1, m.email1].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact row for the member's per-event panel.
function EventTxRowCompact({ tx, event, closed, onEdited }) {
  const [showPreview, setShowPreview] = useStateE(false);
  const [showEdit, setShowEdit] = useStateE(false);
  const canEdit = !closed && window.can('transactions', 'edit');
  // Resolve the event name from the global cache when this row belongs to a
  // different event in the same scheme. The current event is just `event`.
  const rowEv = tx.eventId === event?.id
    ? event
    : (window.QH_DATA?.events || []).find(e => e.id === tx.eventId);
  const evLabel = rowEv?.name || (tx.eventId ? tx.eventId : '—');
  const isCurrent = tx.eventId === event?.id;
  return (
    <>
      <tr>
        <td className="font-mono text-xs">{(tx.date || '').slice(0, 10)}</td>
        <td className="text-xs" title={evLabel}>
          <span style={{fontWeight: isCurrent ? 600 : 400}}>{evLabel}</span>
          {isCurrent && <span className="muted text-xs" style={{marginLeft:4}}>(this event)</span>}
        </td>
        <td>{tx.type === 'deposit'
          ? <span className="tag pos" style={{textTransform:'none'}}>in</span>
          : <span className="tag neg" style={{textTransform:'none'}}>out</span>}</td>
        <td className={`num right ${tx.type === 'deposit' ? 'pos' : 'neg'}`} style={{fontWeight:600}}>
          {tx.type === 'deposit' ? '+' : '−'}{fmtBHD(tx.amount)}
        </td>
        <td className="text-xs">{tx.bank || '—'}</td>
        <td className="text-xs muted">{tx.note || '—'}</td>
        <td style={{whiteSpace:'nowrap'}}>
          {canEdit && <button className="icon-btn" title="Edit" onClick={() => setShowEdit(true)}><I.Edit s={11}/></button>}
          <button className="icon-btn" title="Preview" onClick={() => setShowPreview(true)}><I.MessageSquare s={11}/></button>
          <a className="icon-btn" title="Send via WhatsApp" target="_blank" rel="noreferrer"
             style={{color:'#25D366',textDecoration:'none'}}
             href={buildWhatsAppLink(tx, event)}><I.WhatsApp s={11}/></a>
        </td>
      </tr>
      {showPreview && <MessagePreviewModal tx={tx} event={event} onClose={() => setShowPreview(false)}/>}
      {showEdit && <TransactionEditModal tx={tx} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); onEdited?.(); }}/>}
    </>
  );
}


window.EventDetailPage = EventDetailPage;
window.EventTransactionPage = EventTransactionPage;
window.MemberSearchPicker = MemberSearchPicker;
